import { ChangeEvent, FocusEvent, ForwardedRef, MouseEvent, ReactElement, RefObject, forwardRef } from 'react';

import Button from '@/common/Button';
import Calendar from '@/components/Calendar/new';
import { DATE_FORMAT } from '@/constants';
import { DateInputTypes } from '@/common/DateInput/types';
import { Dayts } from '@/utils';
import Group from '@/common/Group';
import { isCompleteDate } from '@/common/DateInput/utils';

export type DateInputPopoverProps = {
  value: string;
  format?: string;
  target: RefObject<DateInputTypes.RefType>;
  onChange: (
    value: string,
    focus?: boolean,
    event?: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
  ) => string;
  display?: boolean;
};

const DateInputPopover = forwardRef(
  ({ value, format = DATE_FORMAT, target, onChange, display }: DateInputPopoverProps, ref: ForwardedRef<HTMLDivElement>): ReactElement => {
    const handleChange = (newValue: string): void => {
      newValue = new Dayts().setDate(newValue).format(format);
      onChange(newValue, true);
    };
    // Only treat the typed value as a real selected date once it's complete
    const calendarValue = isCompleteDate(value, format) ? value : undefined;
    const Content = (
      <div className="DateInput-Interface" ref={ref}>
        <Calendar value={calendarValue} onChange={handleChange} key={calendarValue} />
      </div>
    );
    if (display !== undefined && display !== false) return Content;
    return (
      <>
        {Content}
        <Group className="DateInput-Controls">
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              const now = new Dayts().format(format);
              onChange(now, true);
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Today
          </Button>
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              target.current?.blur();
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Ok
          </Button>
        </Group>
      </>
    );
  }
);
DateInputPopover.displayName = 'DateInputPopover';

export default DateInputPopover;
